import { createBoundedLruCache } from './table.util';

import type { TFilters, TSorts } from '@client/hooks/useTable';
import type { ExtendedColumnType, TColumns } from '@client/types/antd.type';
import type { ELocales, ICommonDataType, IObjectId } from '@common/types';
import type { TableProps } from 'antd';

export interface ISelectItemProps {
  label: string;
  value: string;
  disabled?: boolean;
}

/**
 * Get the effective key for a column.
 * @param {TColumns} item - The column item.
 * @returns {string} - The effective key.
 */
export const getColumnEffectiveKey = <T extends object>(
  item: ExtendedColumnType<T> | TColumns<T>,
): string => {
  const k = (item as ExtendedColumnType<T> | TColumns<T>).key as string;
  const di = (item as ExtendedColumnType<T> | TColumns<T>).dataIndex as string;
  if (k) return String(k);
  return String(di);
};

/**
 * Get the label for a column.
 * @param {TColumns} item - The column item.
 * @returns {string} - The label.
 */
export const getColumnLabel = <T extends object>(
  item: ExtendedColumnType<T> | TColumns<T>,
): string => {
  const title = (item as any).title as any;
  const fallback = getColumnEffectiveKey(item);

  if (typeof title === 'string' || typeof title === 'number')
    return String(title);

  if (title && typeof title === 'object' && 'props' in title) {
    const children = (title as any).props?.children;
    if (typeof children === 'string' || typeof children === 'number')
      return String(children);

    if (Array.isArray(children)) {
      const parts = children
        .filter((ch: any) => typeof ch === 'string' || typeof ch === 'number')
        .map((ch: any) => String(ch).trim())
        .filter(Boolean);
      if (parts.length) return parts.join(' ').trim();
    }
  }

  return fallback;
};

/**
 * Filter out columns based on the selected columns.
 * @param {ExtendedColumnType[]} columns - The array of columns.
 * @param {Array<TColumns[number]['key']>} selectedColumns - The array of selected columns.
 * @returns {ExtendedColumnType[]} - The filtered array of columns.
 */
export const filterOutColumns = <T extends object>(
  columns: ExtendedColumnType<T>[],
  selectedColumns: Array<TColumns<T>[number]['key']>,
): ExtendedColumnType<T>[] =>
  columns.map((item) => ({
    ...item,
    hidden:
      item.concealable &&
      !selectedColumns.includes(getColumnEffectiveKey(item) as string),
  }));

type TColumnsCache<T> = {
  namespace?: string;
  locale: ELocales;
  sortedInfo: TSorts;
  filteredInfo: TFilters;
  entities: T[];
  filterIn: string[];
  onDelete?: (entity: T) => void;
  onEdit?: (id: string) => void;
};

const columnsStore = createBoundedLruCache<string, TableProps<any>['columns']>(
  100,
);

const getUrlNamespace = (): string => {
  if (typeof window === 'undefined') return '';
  const segments = window.location.pathname.split('/').filter(Boolean);
  return segments.slice(0, 2).join('/') || '';
};

export const columnsCache = <T extends IObjectId>(args: TColumnsCache<T>) => {
  const {
    namespace = getUrlNamespace(),
    sortedInfo,
    filteredInfo,
    entities,
    locale,
    filterIn = [],
    onEdit,
    onDelete,
  } = args;

  // Empty-object sort ({}) and null both mean "no active sort".
  const sortKey = Array.isArray(sortedInfo)
    ? sortedInfo.map((s) => `${s.columnKey}:${s.order}`).join(',')
    : sortedInfo?.columnKey
      ? `${sortedInfo.columnKey}:${sortedInfo.order}`
      : 'null';

  // Sort keys before serialising so {a,b} and {b,a} produce the same string.
  const filterKey = filteredInfo
    ? JSON.stringify(filteredInfo, Object.keys(filteredInfo).sort())
    : 'null';

  // Use entity IDs so a same-length content change still invalidates.
  // Guard against non-array input (e.g. an HTML error body that slipped
  // through as data) so a bad response never crashes column rendering.
  const safeEntities = Array.isArray(entities) ? entities : [];
  const entitiesKey = safeEntities.map((e) => e.id ?? e.uid ?? '').join(',');

  const cacheKey = [
    locale,
    sortKey,
    filterKey,
    entitiesKey,
    !!onEdit,
    !!onDelete,
    filterIn.join(','),
    namespace,
  ].join(':');

  const cached = columnsStore.get(cacheKey) as
    TableProps<T>['columns'] | undefined;

  const updateCached = (
    result: ICommonDataType[],
  ): TableProps<T>['columns'] => {
    columnsStore.set(cacheKey, result as unknown as TableProps<T>['columns']);
    return result as TableProps<T>['columns'];
  };

  return { cached, updateCached };
};
